import React, { useEffect, useMemo, useState, createContext, useContext, useCallback, useRef } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Scale, LayoutDashboard, Users, Search, ShieldAlert, FileText, Sparkles,
  Radar, Gavel, Activity, Calculator, CreditCard, Settings,
  ChevronRight, ChevronLeft, X, Bell, HelpCircle, PlayCircle,
} from "lucide-react";
import JeaButton from "@/components/jea/JeaButton";
import SignUpModal from "@/components/jea/SignUpModal";
import axios from "axios";

const DEMO_TOUR_KEY = "jea_demo_tour_seen";

// Public axios client — no auth headers, uses backend URL from env
const demoClient = axios.create({
  baseURL: (process.env.REACT_APP_BACKEND_URL || "") + "/api",
});

const DemoCtx = createContext(null);
export const useDemo = () => useContext(DemoCtx);

const NAV = [
  { to: "/demo/dashboard",           label: "Dashboard",           Icon: LayoutDashboard },
  { to: "/demo/debtors",             label: "My Judgments",        Icon: Users },
  { to: "/demo/run-query",           label: "Run Query",           Icon: Search },
  { to: "/demo/discovery",           label: "Discovery",           Icon: Radar },
  { to: "/demo/enforcement",         label: "Enforcement",         Icon: Gavel },
  { to: "/demo/monitoring",          label: "Monitoring",          Icon: Activity },
  { to: "/demo/bankruptcy-watch",    label: "Bankruptcy Watch",    Icon: ShieldAlert },
  { to: "/demo/interest-calculator", label: "Interest Calculator", Icon: Calculator },
  { to: "/demo/reports",             label: "Reports",             Icon: FileText },
  { to: "/demo/billing",             label: "Billing",             Icon: CreditCard },
  { to: "/demo/settings",            label: "Settings",            Icon: Settings },
];

const NavItem = ({ item, tourAttr }) => (
  <NavLink
    to={item.to}
    end={item.end}
    data-testid={`demo-nav-${item.to.replace(/\//g, "-")}`}
    data-tour={tourAttr}
    className={({ isActive }) =>
      `h-12 flex items-center gap-3 px-4 text-[14px] font-medium transition-colors ` +
      (isActive
        ? "text-accent-pink bg-bg-tertiary border-l-[3px] border-accent-pink"
        : "text-text-secondary hover:bg-bg-tertiary hover:text-white border-l-[3px] border-transparent")
    }
  >
    <item.Icon size={16} />
    <span>{item.label}</span>
  </NavLink>
);

const DemoShell = () => {
  const nav = useNavigate();
  const location = useLocation();
  const [signup, setSignup] = useState({ open: false, feature: "this feature" });
  const [tourStep, setTourStep] = useState(-1); // -1 = not running

  // Auto-launch on first visit to /demo
  useEffect(() => {
    if (location.pathname !== "/demo") return;
    let seen = false;
    try { seen = localStorage.getItem(DEMO_TOUR_KEY) === "true"; } catch { /* */ }
    if (!seen) startTour();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startTour = () => setTourStep(0);
  const skipTour = () => {
    setTourStep(-1);
    try { localStorage.setItem(DEMO_TOUR_KEY, "true"); } catch { /* */ }
  };
  const finishTour = () => {
    setTourStep(-1);
    try { localStorage.setItem(DEMO_TOUR_KEY, "true"); } catch { /* */ }
    nav("/register");
  };

  // Fires on Next / Prev — handles auto-navigation between steps
  const goToStep = useCallback(async (nextIdx) => {
    if (nextIdx < 0 || nextIdx >= TOUR_STEPS.length) return;
    const step = TOUR_STEPS[nextIdx];
    if (step.route === "__debtor_first__") {
      try {
        const { data } = await demoClient.get("/demo/debtors");
        const first = (data.debtors || [])[0];
        if (first) nav(`/demo/debtors/${first.id}`);
      } catch { /* */ }
    } else if (step.route) {
      nav(step.route);
    }
    setTourStep(nextIdx);
  }, [nav]);

  const openSignUp = useCallback((feature = "this feature") => {
    setSignup({ open: true, feature });
  }, []);

  const ctx = useMemo(() => ({ openSignUp, startTour }), [openSignUp]);

  return (
    <DemoCtx.Provider value={ctx}>
      <div className="min-h-screen bg-bg-primary text-white flex" data-testid="demo-shell">
        {/* Sidebar */}
        <aside className="w-[240px] shrink-0 bg-bg-secondary border-r border-border-default flex flex-col" data-tour="sidebar">
          <div className="px-4 py-5">
            <Link to="/demo" className="flex items-center gap-2">
              <Scale className="text-accent-pink" size={20} />
              <span className="text-accent-pink font-extrabold text-[16px]">Judgment Enforcement AI</span>
            </Link>
            <div className="mt-3 h-px bg-accent-pink/20" />
          </div>
          <nav className="flex-1 flex flex-col py-2 overflow-y-auto" data-testid="demo-sidebar-nav">
            <NavLink
              to="/demo"
              end
              data-testid="demo-nav-home"
              className={({ isActive }) =>
                `h-12 flex items-center gap-3 px-4 text-[14px] font-medium transition-colors ` +
                (isActive
                  ? "text-accent-pink bg-bg-tertiary border-l-[3px] border-accent-pink"
                  : "text-text-secondary hover:bg-bg-tertiary hover:text-white border-l-[3px] border-transparent")
              }
            >
              <Sparkles size={16} />
              <span>Home</span>
            </NavLink>
            {NAV.map((item) => (
              <NavItem key={item.to} item={item} />
            ))}
          </nav>
          <div className="p-4 border-t border-border-default">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-accent-pink/20 border border-accent-pink/40 flex items-center justify-center text-accent-pink font-bold">D</div>
              <div>
                <div className="text-white text-[13px] font-semibold">Demo User</div>
                <div className="text-text-secondary text-[10px] tracking-[0.08em]">PREVIEW MODE</div>
              </div>
            </div>
          </div>
        </aside>

        {/* Main */}
        <div className="flex-1 min-w-0 flex flex-col">
          <header className="h-16 border-b border-border-default flex items-center justify-between px-6" data-testid="demo-header">
            <div className="text-white text-[16px] font-bold">{titleFor(location.pathname)}</div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => openSignUp("real-time notifications")}
                className="relative p-2 rounded-full hover:bg-bg-tertiary text-text-secondary hover:text-accent-pink transition-colors"
                data-testid="demo-header-bell"
                title="Notifications"
              >
                <Bell size={18} />
                <span className="absolute -top-0.5 -right-0.5 bg-danger text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                  3
                </span>
              </button>
              <button
                type="button"
                onClick={startTour}
                className="p-2 rounded-full hover:bg-bg-tertiary text-text-secondary hover:text-accent-pink transition-colors"
                data-testid="demo-header-help"
                title="Restart tour"
              >
                <HelpCircle size={18} />
              </button>
              <div className="flex items-center gap-2" data-tour="signup-cta">
                <JeaButton variant="outline-neutral" size="sm" onClick={() => nav("/login")} data-testid="demo-signin">
                  Sign In
                </JeaButton>
                <JeaButton size="sm" onClick={() => nav("/register")} data-testid="demo-signup">
                  Sign Up to Get Started
                </JeaButton>
              </div>
            </div>
          </header>

          <div className="bg-bg-tertiary border-l-4 border-accent-pink px-6 py-3 flex items-center justify-between flex-wrap gap-3" data-testid="demo-nag-banner">
            <div className="text-text-secondary text-[13px]">
              You're exploring the demo.{" "}
              <button type="button" onClick={startTour} className="underline text-accent-pink hover:text-accent-pink-light font-semibold" data-testid="demo-restart-tour">
                Restart tour
              </button>
              . Sign up to save your work and unlock real broker queries.
            </div>
            <JeaButton size="sm" onClick={() => nav("/register")} data-testid="demo-banner-signup">Sign Up Free</JeaButton>
          </div>

          <main className="flex-1 p-8 max-w-[1320px] w-full mx-auto">
            <Outlet />
          </main>
        </div>

        <SignUpModal
          open={signup.open}
          feature={signup.feature}
          onClose={() => setSignup({ open: false, feature: "this feature" })}
        />

        <DemoTour
          step={tourStep}
          onGoto={goToStep}
          onSkip={skipTour}
          onFinish={finishTour}
        />
      </div>
    </DemoCtx.Provider>
  );
};

const titleFor = (pathname) => {
  if (pathname === "/demo") return "Explore the Demo";
  const item = NAV.find((n) => pathname === n.to || pathname.startsWith(n.to + "/"));
  return item?.label || "Explore the Demo";
};

/* ─── 10-step guided tour ─── */
const TOUR_STEPS = [
  { title: "Welcome to Judgment Enforcement AI",
    body: "Take a 2-minute tour to see how we help judgment creditors recover more, faster. Skip anytime.",
    route: "/demo" },
  { title: "Your portfolio at a glance",
    body: "Track active judgments, queries run, reports generated, and upcoming actions — all from your dashboard.",
    route: "/demo/dashboard" },
  { title: "Every judgment. Every case. All searchable.",
    body: "Filter and sort by tier, status, and tags. Every judgment has a full profile with case details, assets, and enforcement history.",
    route: "/demo/debtors" },
  { title: "A complete judgment profile in one view",
    body: "Every judgment gets a complete profile: case details, assets found, critical dates, and a bankruptcy survivability score.",
    route: "__debtor_first__" },
  { title: "One click. Seven data sources. Real answers.",
    body: "Choose a judgment and a broker bundle. Our orchestrator runs Accurint, TLOxp, Work Number, PACER, CourtListener, Docket Alarm, and UniCourt in parallel.",
    route: "/demo/run-query" },
  { title: "Court-aware asset discovery",
    body: "Small claims, county, or federal — we surface the right rules and the right brokers for the job. No manual triage.",
    route: "/demo/discovery" },
  { title: "Three guided enforcement pathways",
    body: "Wage garnishment, bank levy, or property seizure — every pathway includes state-specific exemption checks under 11 U.S.C. and state statute.",
    route: "/demo/enforcement" },
  { title: "Never miss a deadline",
    body: "Track judgment expirations, interest accrual, and recoverable costs across your entire portfolio. Deadlines light up red when they're inside 14 days.",
    route: "/demo/monitoring" },
  { title: "Automatic bankruptcy stay protection",
    body: "PACER filings surface automatically. Recovery Lookback finds money after discharge. Never illegally violate the § 362 automatic stay.",
    route: "/demo/bankruptcy-watch" },
  { title: "Ready to run real queries?",
    body: "Sign up in 60 seconds. Add your first judgment, and start recovering money that's currently sitting on the table.",
    route: "/demo" },
];

const DemoTour = ({ step, onGoto, onSkip, onFinish }) => {
  const isRunning = step >= 0 && step < TOUR_STEPS.length;
  const s = isRunning ? TOUR_STEPS[step] : null;
  const isLast = step === TOUR_STEPS.length - 1;
  const isFirst = step === 0;

  // Kick off route change when the tour opens on step 0 or state changes externally
  const lastRouteRef = useRef(null);
  useEffect(() => {
    if (!isRunning) return;
    if (lastRouteRef.current === step) return;
    lastRouteRef.current = step;
    // Fire the effect via onGoto to reuse the async navigation logic
    onGoto(step);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  return (
    <AnimatePresence>
      {isRunning && (
        <motion.div
          key="tour-card"
          initial={{ opacity: 0, y: 20, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.96 }}
          transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
          className="fixed bottom-6 right-6 z-[91] w-full max-w-[420px] bg-bg-secondary border border-accent-pink/60 rounded-[12px] p-6 shadow-elevated pointer-events-auto"
          style={{
            boxShadow:
              "0 8px 40px rgba(0,0,0,0.60), 0 0 0 4px rgba(251, 111, 146, 0.15), 0 0 32px rgba(251, 111, 146, 0.25)",
          }}
          data-testid="demo-tour-card"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="text-accent-pink text-[11px] tracking-[0.08em] font-semibold" data-testid="demo-tour-progress">
              TOUR · STEP {step + 1} OF {TOUR_STEPS.length}
            </div>
            <button
              type="button"
              onClick={onSkip}
              className="p-1 text-text-secondary hover:text-white"
              data-testid="demo-tour-skip"
              aria-label="Skip tour"
            >
              <X size={14} />
            </button>
          </div>

          {/* Progress dots */}
          <div className="flex gap-1 mb-4" data-testid="demo-tour-dots">
            {TOUR_STEPS.map((_, i) => (
              <div
                key={i}
                className={
                  "h-1 flex-1 rounded-full transition-colors " +
                  (i <= step ? "bg-accent-pink" : "bg-border-default")
                }
              />
            ))}
          </div>

          <h3 className="text-white font-bold text-[17px] leading-tight" data-testid="demo-tour-title">
            {s.title}
          </h3>
          <p className="text-text-secondary text-[13px] leading-relaxed mt-2" data-testid="demo-tour-body">
            {s.body}
          </p>

          <div className="mt-5 flex items-center justify-between">
            <button
              type="button"
              onClick={onSkip}
              className="text-text-secondary text-[13px] hover:text-white font-medium"
              data-testid="demo-tour-skip-link"
            >
              Skip tour
            </button>
            <div className="flex items-center gap-2">
              {!isFirst && (
                <button
                  type="button"
                  onClick={() => onGoto(step - 1)}
                  className="h-9 px-3 rounded-[8px] border border-border-default text-text-secondary hover:text-white hover:border-accent-pink/40 text-[13px] font-semibold inline-flex items-center gap-1 transition-colors"
                  data-testid="demo-tour-prev"
                >
                  <ChevronLeft size={14} /> Prev
                </button>
              )}
              {isLast ? (
                <JeaButton size="sm" onClick={onFinish} className="gap-2" data-testid="demo-tour-finish">
                  <PlayCircle size={14} /> Sign Up Free
                </JeaButton>
              ) : (
                <JeaButton size="sm" onClick={() => onGoto(step + 1)} className="gap-1" data-testid="demo-tour-next">
                  Next <ChevronRight size={14} />
                </JeaButton>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export { demoClient };
export default DemoShell;
